import {
  useChecklistItemResponseSchema,
  type ChecklistResponse
} from "@swivu/client-api";
import { ChatBubbleLeftIcon } from "@heroicons/react/20/solid";
import { useEffect, useLayoutEffect, useRef } from "react";
import { QueryClient } from "@tanstack/react-query";

import { DrawerOrDialog } from "../DrawerOrDialog.js";
import { Toast } from "../Toast.js";
import { Textarea } from "../Textarea.js";
import { Button } from "../Button.js";
import { cn } from "../../utils/cn.js";
import { useIsGummy } from "../../hooks/useGummyTheme.js";
import { Spinner } from "../Spinner.js";

type ChecklistCommentProps = {
  isOpen: boolean;
  handleClose: () => void;
  checklist_id: number;
  item: ChecklistResponse["items"][number];
  queryClient: QueryClient;
};

const ChecklistCommentWrapper = ({
  isOpen,
  handleClose,
  item,
  checklist_id,
  queryClient
}: ChecklistCommentProps) => {
  const isGummy = useIsGummy();
  return (
    <DrawerOrDialog.Root
      drawer={{
        open: isOpen,
        onOpenChange: (isOpen) => {
          if (!isOpen) {
            handleClose();
          }
        }
      }}
      dialog={{
        open: isOpen,
        onOpenChange: (isOpen) => {
          if (!isOpen) {
            handleClose();
          }
        }
      }}
    >
      <DrawerOrDialog.Content
        className={cn("bg-mono-100/90 dark:bg-mono-950/90")}
      >
        <DrawerOrDialog.Title
          className={cn(
            "mobile-pref-checklist-comment-title mb-2 flex items-start gap-x-4 px-4 py-2 text-left md:px-0.5",
            isGummy
              ? "dark:text-white"
              : "dark:border-mono-700/50 dark:text-mono-200 border-b"
          )}
        >
          {isGummy ? (
            <span
              className={cn(
                "flex size-10 flex-none items-center justify-center rounded-full text-white",
                "bg-tile-calendar"
              )}
              style={{ textShadow: "var(--text-shadow-on-color)" }}
            >
              <ChatBubbleLeftIcon className="size-5" />
            </span>
          ) : (
            <ChatBubbleLeftIcon className="mobile-pref-control-icon h-5 w-5" />
          )}
          <div className="min-w-0 flex-1">
            <h3
              className={cn(
                "mobile-pref-section-title",
                isGummy &&
                  "text-[20px] font-extrabold leading-tight text-mono-900 dark:text-white"
              )}
            >
              Add comment
            </h3>
            <p
              className={cn(
                "mobile-pref-card-meta truncate",
                isGummy
                  ? "mt-1 text-[11px] font-extrabold uppercase tracking-widest text-mono-500 dark:text-mono-400"
                  : "text-sm opacity-50"
              )}
            >
              {item.description}
            </p>
          </div>
        </DrawerOrDialog.Title>
        <div className="dark:text-mono-200 px-4 pb-6 pt-4 md:px-0.5 md:pb-0 md:pt-0">
          <ChecklistComment
            item={item}
            onComplete={handleClose}
            checklist_id={checklist_id}
            queryClient={queryClient}
          />
        </div>
      </DrawerOrDialog.Content>
    </DrawerOrDialog.Root>
  );
};

type UploadChecklistImgProps = {
  onComplete: () => void;
  item: ChecklistResponse["items"][number];
  checklist_id: number;
  queryClient: QueryClient;
};

const ChecklistComment = ({
  item,
  onComplete,
  checklist_id,
  queryClient
}: UploadChecklistImgProps) => {
  const isGummy = useIsGummy();
  const ref = useRef<HTMLFormElement>(null);
  const mutation = useChecklistItemResponseSchema({
    onSuccess: async (_data) => {
      await queryClient.refetchQueries({
        queryKey: ["checklist", { checklist_id }]
      });
    }
  });

  const textarea_ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    setTimeout(() => {
      textarea_ref?.current?.focus();
    }, 100);
  }, []);

  useEffect(() => {
    if (mutation.isError) {
      Toast.toast({
        title: "Could not add comment",
        description: mutation.error.message
      });
    }
  }, [mutation.isError]);

  useEffect(() => {
    if (mutation.isSuccess) {
      ref.current?.reset();
      onComplete();
    }
  }, [mutation.isSuccess]);

  const handleUpload = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const comment = data.get("comment");
    if (comment === "") {
      Toast.toast({
        title: "Comment is empty",
        description: "Please write a comment"
      });
      return;
    }
    if (typeof comment === "string") {
      mutation.mutate({
        id: item.id,
        comment: comment,
        status: item.status,
        api: "wf"
      });
    }
  };

  return (
    <form
      onSubmit={handleUpload}
      className={`mobile-pref-checklist-comment-form space-y-6 md:mt-6`}
      ref={ref}
    >
      <Textarea
        ref={textarea_ref}
        autoFocus={true}
        name={`comment`}
        placeholder={`Write your checklist comment here`}
        className={cn(
          "mobile-pref-control mobile-pref-rich-text",
          isGummy
            ? cn(
                "rounded-surface-md border-0 bg-white dark:bg-mono-950 text-mono-900 dark:text-white",
                "placeholder:text-mono-400 dark:placeholder:text-mono-500",
                "font-extrabold",
                "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40"
              )
            : "border-mono-300 dark:bg-mono-950 dark:border-mono-700/50 bg-white"
        )}
        style={isGummy ? { fontFamily: "var(--font-body)" } : undefined}
      />
      <div
        className={`mobile-pref-checklist-comment-actions flex flex-col-reverse gap-3 md:flex-row md:justify-end md:gap-4`}
      >
        {isGummy ? (
          <>
            <button
              type="button"
              onClick={onComplete}
              disabled={mutation.isPending}
              className={cn(
                "mobile-pref-control inline-flex h-12 items-center justify-center rounded-surface-md px-5 text-base font-black",
                "bg-tile-neutral text-mono-800",
                "dark:from-mono-800 dark:to-mono-900 dark:text-mono-100",
                "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
                "motion-reduce:transform-none motion-reduce:transition-none",
                "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40",
                "disabled:pointer-events-none disabled:opacity-50",
                "w-full md:w-auto md:flex-none"
              )}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className={cn(
                "mobile-pref-control inline-flex h-12 items-center justify-center gap-2 rounded-surface-md px-5 text-base font-black",
                "bg-gradient-to-b from-primary-400 to-primary-600 text-white",
                "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
                "motion-reduce:transform-none motion-reduce:transition-none",
                "outline-none focus-visible:ring-4 focus-visible:ring-white/80",
                "disabled:pointer-events-none disabled:opacity-50 disabled:grayscale",
                mutation.isPending && "cursor-wait",
                "w-full md:w-auto md:flex-none"
              )}
              style={{ textShadow: "var(--text-shadow-on-color)" }}
            >
              {mutation.isPending ? (
                <Spinner className="h-5 w-5" />
              ) : (
                "Save comment"
              )}
            </button>
          </>
        ) : (
          <>
            <Button
              onClick={onComplete}
              type="button"
              action="tertiary"
              className="mobile-pref-control"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              action="secondary"
              loading={mutation.isPending}
              className="mobile-pref-control"
            >
              Save comment
            </Button>
          </>
        )}
      </div>
    </form>
  );
};

export default ChecklistCommentWrapper;
